import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm, crmFileUrl, uploadCrmDoc } from './crmApi';
import type { CrmAuth, P2PAction, P2PCase, P2PPay, P2PPrices, P2PTab } from './crmApi';
import { Empty, SkillChips, Spinner, Tabs, TONE, fmtPhone, fmtWhen, fromLocalInput, inputCls, labelCls, purpleBg, toLocalInput } from './ui';

// ── PATH TO PLACEMENT: the P2P board (server/routes/p2p.js) ──
// Every P2P meeting a salesperson books lands here. The first Lead Manager to
// tap Take has it: the meeting link, joined or not (or no response: the
// salesperson's No Response list), the test fee link. Another Lead Manager can
// take it over. Once the test fee is paid, the student goes to a Career
// Strategist too (the same card, on their P2P Students page,
// pages/mentor/P2PStudents), and the Lead Manager keeps them on Paid students:
// both do the test, the result, the balance, the ATS offer, a refund, and
// approve or change the date a student picks for the rest of the balance. The
// other Lead Managers see them with no buttons. The admin can do any step, and
// makes and sends the ATS resume. A salesperson sees the P2P meetings
// they booked (and the same card on the lead page). Mirrors the app's
// crm_p2p_screen.dart.

const rupees = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const waUrl = (phone: string, text: string) => `https://wa.me/91${phone}?text=${encodeURIComponent(text)}`;
const STEP_STYLE: Record<string, string> = {
  meeting: 'bg-indigo-100 text-indigo-800', fee: 'bg-amber-100 text-amber-800', test: 'bg-sky-100 text-sky-800',
  pass: 'bg-green-100 text-green-800', fail: 'bg-rose-100 text-rose-800', ats: 'bg-fuchsia-100 text-fuchsia-800', done: 'bg-gray-100 text-gray-700',
};
const PAY_STYLE: Record<string, string> = { Pending: 'bg-amber-100 text-amber-800', Paid: 'bg-green-100 text-green-800', Cancelled: 'bg-gray-100 text-gray-500' };
const btn = 'px-3 py-2 rounded-xl text-xs font-bold transition-colors disabled:opacity-60';
const btnLine = `${btn} border-2 border-gray-200 bg-white text-gray-700 hover:bg-gray-50`;
const btnGo = `${btn} text-white`;

const pdfUrl = (b64: string) => URL.createObjectURL(new Blob([Uint8Array.from(atob(b64), c => c.charCodeAt(0))], { type: 'application/pdf' }));
// A receipt PDF from the server: saved under its own name, or shown in the tab
// opened on the click (popup blockers allow only that one)
function savePdf(b64: string, fileName: string) {
  const url = pdfUrl(b64);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
function showPdf(b64: string, win: Window | null) {
  const url = pdfUrl(b64);
  if (win) win.location.href = url; else window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

type Form = '' | 'link' | 'move' | 'cancel' | 'notPaying' | 'test' | 'result' | 'ats' | 'restDate';
const VIA: Record<string, string> = { Website: 'P2P page', Call: 'Call', 'Payment page': 'Payment page' };
// YYYY-MM-DD in India, n days from today (the rest's date picker)
const istDay = (n = 0) => new Date(Date.now() + 5.5 * 3600e3 + n * 86400e3).toISOString().slice(0, 10);

const PayRow: React.FC<{ p: P2PPay }> = ({ p }) => {
  const copy = () => navigator.clipboard?.writeText(p.url).then(() => toast.success('Payment link copied'), () => {});
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs py-1.5 border-t border-gray-100 first:border-t-0">
      <span className="font-bold text-gray-800">{p.label}</span>
      <span className="font-bold text-gray-900">{rupees(p.amount)}</span>
      <span className={`px-2 py-0.5 rounded-full font-bold ${PAY_STYLE[p.status] || ''}`}>{p.status}</span>
      {p.status === 'Paid' && <span className="text-gray-500">{fmtWhen(p.paidAt)}{p.method ? ` · ${p.method}` : ''}{p.restAfter ? ` · ${rupees(p.restAfter)} left` : ''}</span>}
      {p.status === 'Pending' && p.payBy && <span className="text-gray-500">pay by {fmtWhen(p.payBy)}</span>}
      {p.refund && <span className={`px-2 py-0.5 rounded-full font-bold ${p.refund.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-800'}`}>Refund {p.refund.status === 'processed' ? 'done' : p.refund.status} · {rupees(p.refund.amount)}</span>}
      {p.status === 'Pending' && (
        <span className="flex gap-1.5 ml-auto">
          <button type="button" onClick={copy} className="font-bold text-purple-700 hover:underline">Copy link</button>
          <a href={p.url} target="_blank" rel="noreferrer" className="font-bold text-purple-700 hover:underline">Open</a>
        </span>
      )}
    </li>
  );
};

// One P2P student: where they are, what to do next, and the buttons for it
// (openResume: a Career Strategist opens the resume their own way)
export const P2PCard: React.FC<{ auth: CrmAuth | { csToken: string }; c: P2PCase; onChange: (c: P2PCase) => void; openResume?: () => void }> = ({ auth, c, onChange, openResume }) => {
  const [form, setForm] = useState<Form>('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(c.link);
  const [when, setWhen] = useState(() => toLocalInput(c.testAt || c.meetingAt || new Date()));
  const [where, setWhere] = useState(c.testWhere || '');
  const [reason, setReason] = useState('');
  const [score, setScore] = useState('');
  const [atsFile, setAtsFile] = useState<File | null>(null);
  const [restDay, setRestDay] = useState('');
  const can = c.can;

  const act = async (action: P2PAction, more: Record<string, unknown> = {}, done = '') => {
    setBusy(true);
    try {
      const r = await crm.p2pAct(auth, action, c.id, more);
      if (done) toast.success(done);
      setForm('');
      onChange(r.case);
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    if (openResume) { openResume(); return; }
    try {
      const r = await crm.resumeLink(auth as CrmAuth, c.leadId);
      window.open(crmFileUrl(r.url), '_blank', 'noopener');
    } catch (e: any) { toast.error(e.message); }
  };
  const sendAts = async () => {
    if (!atsFile) { toast.error('Pick the ATS resume file'); return; }
    setBusy(true);
    try {
      const up = await uploadCrmDoc(auth as CrmAuth, c.leadId, 'ats', atsFile);
      const r = await crm.p2pAct(auth, 'p2pSendAts', c.id, { file: up.file });
      toast.success(`The ATS resume went to ${c.name} by email`);
      setForm('');
      onChange(r.case);
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const open = (f: Form) => {
    setForm(form === f ? '' : f);
    if (f === 'move') setWhen(toLocalInput(c.meetingAt || new Date()));
    if (f === 'test') setWhen(toLocalInput(c.testAt || new Date(Date.now() + 24 * 3600e3)));
    if (f === 'restDate' && c.rest) setRestDay(new Date(new Date(c.rest.at).getTime() + 5.5 * 3600e3).toISOString().slice(0, 10));
  };
  const resumeLate = c.step === 'meeting' && c.resumeDueAt && new Date(c.resumeDueAt).getTime() < Date.now();
  const atsPending = c.payments.find(p => p.kind === 'ats' && p.status === 'Pending');

  return (
    <article className="rounded-2xl border-2 border-gray-100 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-start gap-2 justify-between">
        <div className="min-w-0">
          <p className="font-bold text-gray-900">{c.name || fmtPhone(c.phone)} <span className="text-xs font-semibold text-gray-400">#{c.leadNo}</span></p>
          <p className="text-xs text-gray-500 mt-0.5">
            <a href={`tel:+91${c.phone}`} className="font-semibold text-purple-700">{fmtPhone(c.phone)}</a>
            {c.email ? ` · ${c.email}` : ''}{c.degree || c.passedOutYear ? ` · ${[c.degree, c.passedOutYear].filter(Boolean).join(' ')}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${STEP_STYLE[c.step] || ''}`}>{c.stepLabel}</span>
          {c.via && <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">{VIA[c.via] || c.via}</span>}
          {c.stuck && <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-700">⚠️ {c.stuck}</span>}
        </div>
      </div>
      <SkillChips skills={c.skills} />

      <p className={`text-sm font-bold ${TONE[c.next.tone] || ''}`}>{c.next.text}</p>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
        {c.meetingAt
          ? <div><dt className="inline font-semibold text-gray-400">Meeting: </dt><dd className="inline">{c.meetingWhen} ({c.minutes} min){c.link && <> · <a href={c.link} target="_blank" rel="noreferrer" className="font-semibold text-purple-700">link</a></>}</dd></div>
          : <div><dt className="inline font-semibold text-gray-400">Came: </dt><dd className="inline">paid the test fee on the payment page</dd></div>}
        <div><dt className="inline font-semibold text-gray-400">Lead Manager: </dt><dd className="inline">{c.lmName || (c.meetingAt ? 'not taken yet' : '—')}</dd></div>
        {c.step !== 'meeting' && c.step !== 'fee' && <div><dt className="inline font-semibold text-gray-400">Career Strategist: </dt><dd className="inline font-semibold">{c.csName || 'nobody yet'}</dd></div>}
        {c.bookedByName && <div><dt className="inline font-semibold text-gray-400">Booked by: </dt><dd className="inline">{c.bookedByName} · {fmtWhen(c.bookedAt)}</dd></div>}
        {c.testAt && <div><dt className="inline font-semibold text-gray-400">Test: </dt><dd className="inline">{c.testWhen} · {/^https?:/.test(c.testWhere) ? <a href={c.testWhere} target="_blank" rel="noreferrer" className="font-semibold text-purple-700">online</a> : c.testWhere}</dd></div>}
        {c.result && <div><dt className="inline font-semibold text-gray-400">Result: </dt><dd className="inline font-bold">{c.result === 'Pass' ? 'Passed' : 'Failed'}{c.score ? ` · ${c.score}` : ''}</dd></div>}
        {c.outcome && <div><dt className="inline font-semibold text-gray-400">Outcome: </dt><dd className="inline font-bold">{c.outcome}{c.reason ? ` · ${c.reason}` : ''}</dd></div>}
      </dl>
      {c.note && <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2"><b className="text-gray-400">From the salesperson:</b> {c.note}</p>}
      {c.step === 'meeting' && c.resumeDueAt && (
        <p className={`text-xs font-bold rounded-lg px-3 py-2 ${resumeLate ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}>📄 Resume {resumeLate ? 'late: it was due' : 'due'} by {fmtWhen(c.resumeDueAt)}</p>
      )}

      {c.payments.length > 0 && <ul className="rounded-xl bg-gray-50 px-3 py-1">{c.payments.map(p => <PayRow key={p.id} p={p} />)}</ul>}

      {c.balance && c.balance.paid > 0 && c.balance.due > 0 && (
        <div className="rounded-xl bg-green-50 px-3 py-2">
          <p className="text-xs font-bold text-green-800">Balance: {rupees(c.balance.paid)} paid of {rupees(c.balance.total)} · {rupees(c.balance.due)} left</p>
          <div className="mt-1.5 h-1.5 rounded-full bg-green-100 overflow-hidden"><div className="h-full bg-green-600" style={{ width: `${Math.round((c.balance.paid / c.balance.total) * 100)}%` }} /></div>
        </div>
      )}
      {c.rest && (
        <div className={`rounded-xl px-3 py-2 ${c.rest.status === 'asked' ? 'bg-amber-50' : 'bg-gray-50'}`}>
          <p className={`text-xs font-bold ${c.rest.status === 'asked' ? 'text-amber-800' : 'text-gray-700'}`}>
            📅 The rest by {c.rest.when} · {c.rest.status === 'asked' ? 'the student asked for this date: waiting for your OK' : `${c.rest.changed ? 'set' : 'approved'} by ${c.rest.byName}`}
          </p>
          {can.restDate && (
            <div className="flex flex-wrap gap-2 mt-2">
              {c.rest.status === 'asked' && <button disabled={busy} onClick={() => act('p2pRestDate', {}, `Approved: ${c.name} gets an email`)} className={`${btnGo} bg-green-600 hover:bg-green-700`}>✅ Approve {c.rest.when}</button>}
              <button disabled={busy} onClick={() => open('restDate')} className={btnLine}>📅 Change the date</button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {can.take && <button disabled={busy} onClick={() => act('p2pTake', {}, c.step === 'meeting' ? `You have ${c.name}'s P2P meeting` : `${c.name} is yours now`)} className={btnGo} style={purpleBg}>🙋 {c.step === 'meeting' ? 'Take this meeting' : 'Take this student'}</button>}
        {can.csTake && <button disabled={busy} onClick={() => act('p2pCsTake', {}, `${c.name} is yours now`)} className={btnGo} style={purpleBg}>🙋 Take this student</button>}
        {can.csTakeOver && <button disabled={busy} onClick={() => { if (window.confirm(`Take ${c.name} over from ${c.csName}?`)) act('p2pCsTake', {}, `${c.name} is yours now`); }} className={btnLine}>🔁 Take over</button>}
        {can.takeOver && <button disabled={busy} onClick={() => { if (window.confirm(`Take ${c.name} over from ${c.lmName}?`)) act('p2pTakeOver', {}, `${c.name} is yours now`); }} className={btnLine}>🔁 Take over</button>}
        {can.link && <button disabled={busy} onClick={() => open('link')} className={btnLine}>🔗 {c.link ? 'Change the link' : 'Add the meeting link'}</button>}
        {can.joined && <>
          <button disabled={busy} onClick={() => act('p2pJoined', { joined: true }, `Joined: the test fee link went to ${c.name}`)} className={`${btnGo} bg-green-600 hover:bg-green-700`}>✅ Joined</button>
          <button disabled={busy} onClick={() => { if (window.confirm(`${c.name} didn't join? The lead goes back to ${c.bookedByName} to book again.`)) act('p2pJoined', { joined: false }, 'Back to the salesperson'); }} className={btnLine}>📞 Didn't join – book again</button>
          <button disabled={busy} onClick={() => { if (window.confirm(`${c.name} didn't join and doesn't answer? The lead goes to ${c.bookedByName}'s No Response list.`)) act('p2pJoined', { joined: 'no-response' }, `No response: on ${c.bookedByName}'s No Response list`); }} className={btnLine}>🔕 No response</button>
        </>}
        {can.setTest && <button disabled={busy} onClick={() => open('test')} className={btnGo} style={purpleBg}>📝 {c.testAt ? 'Change the test time' : 'Set the test time'}</button>}
        {can.result && <button disabled={busy} onClick={() => open('result')} className={btnGo} style={purpleBg}>🏁 Mark the result</button>}
        {can.balance && <button disabled={busy} onClick={() => act('p2pBalance', {}, 'The balance link went to the student')} className={btnLine}>💳 Send the balance link</button>}
        {can.offerAts && <button disabled={busy} onClick={() => act('p2pOfferAts', {}, 'The ATS link went to the student')} className={btnLine}>📄 Offer the ATS resume</button>}
        {can.refund && <button disabled={busy} onClick={() => {
          const fee = c.payments.find(p => p.kind === 'test' && p.status === 'Paid');
          if (window.confirm(`Refund ${rupees(fee ? fee.amount : 0)} to ${c.name}? This can't be undone.`)) act('p2pRefund', {}, 'Refund started');
        }} className={`${btn} border-2 border-rose-200 bg-white text-rose-700 hover:bg-rose-50`}>↩️ Refund {rupees(c.payments.find(p => p.kind === 'test' && p.status === 'Paid')?.amount || 0)}{atsPending ? ' (no ATS)' : ''}</button>}
        {can.sendAts && <button disabled={busy} onClick={() => open('ats')} className={btnGo} style={purpleBg}>📤 Send the ATS resume</button>}
        {can.resend && <button disabled={busy} onClick={() => act('p2pResend', {}, 'Payment link sent again by email')} className={btnLine}>✉️ Resend the payment link</button>}
        {can.notPaying && <button disabled={busy} onClick={() => open('notPaying')} className={btnLine}>🚫 Not paying</button>}
        {can.move && <button disabled={busy} onClick={() => open('move')} className={btnLine}>🕑 Move the meeting</button>}
        {can.cancel && <button disabled={busy} onClick={() => open('cancel')} className={btnLine}>✖️ Cancel</button>}
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={resume} className={`${btn} text-purple-700 hover:bg-purple-50`}>📄 Resume</button>
        {c.step === 'meeting' && <a href={waUrl(c.phone, c.wa.meeting)} target="_blank" rel="noreferrer" className={`${btn} text-green-700 hover:bg-green-50`}>💬 WhatsApp the meeting</a>}
        {c.wa.pay && <a href={waUrl(c.phone, c.wa.pay)} target="_blank" rel="noreferrer" className={`${btn} text-green-700 hover:bg-green-50`}>💬 WhatsApp the payment link</a>}
        {c.wa.test && c.step === 'test' && <a href={waUrl(c.phone, c.wa.test)} target="_blank" rel="noreferrer" className={`${btn} text-green-700 hover:bg-green-50`}>💬 WhatsApp the test</a>}
      </div>

      {form === 'link' && (
        <div className="rounded-xl border-2 border-purple-100 bg-purple-50/40 p-3 space-y-2">
          <label className={labelCls} htmlFor={`l-${c.id}`}>Meeting link (Google Meet, Teams or Zoom, or the whole invite)</label>
          <textarea id={`l-${c.id}`} rows={2} value={link} onChange={e => setLink(e.target.value)} className={`${inputCls} resize-none`} />
          <button disabled={busy || !link.trim()} onClick={() => act('p2pSetLink', { link }, 'The link went to the student by email')} className={btnGo} style={purpleBg}>Save and email it</button>
        </div>
      )}
      {(form === 'move' || form === 'test') && (
        <div className="rounded-xl border-2 border-purple-100 bg-purple-50/40 p-3 space-y-2">
          <label className={labelCls} htmlFor={`w-${c.id}`}>{form === 'move' ? 'New meeting time' : 'Test date and time'}</label>
          <input id={`w-${c.id}`} type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} className={inputCls} />
          {form === 'test' && <>
            <label className={labelCls} htmlFor={`wh-${c.id}`}>Where (paste the online test link, or type "Office")</label>
            <input id={`wh-${c.id}`} value={where} onChange={e => setWhere(e.target.value)} className={inputCls} placeholder="https://… or Office" />
          </>}
          <button disabled={busy || !when || (form === 'test' && !where.trim())}
            onClick={() => (form === 'move' ? act('p2pMove', { meetingAt: fromLocalInput(when) }, 'Moved: the student gets the new time') : act('p2pSetTest', { testAt: fromLocalInput(when), testWhere: where }, 'The test time went to the student'))}
            className={btnGo} style={purpleBg}>{form === 'move' ? 'Move it' : 'Save and email it'}</button>
        </div>
      )}
      {(form === 'cancel' || form === 'notPaying') && (
        <div className="rounded-xl border-2 border-gray-200 p-3 space-y-2">
          <label className={labelCls} htmlFor={`r-${c.id}`}>Why? (optional)</label>
          <input id={`r-${c.id}`} value={reason} onChange={e => setReason(e.target.value)} className={inputCls} placeholder={form === 'cancel' ? 'Busy this week' : 'No money right now'} />
          <button disabled={busy} onClick={() => (form === 'cancel' ? act('p2pCancel', { reason }, 'Cancelled: the lead is back with the salesperson') : act('p2pNotPaying', { reason }, 'Closed: not converted'))}
            className={`${btn} border-2 border-gray-300 bg-white text-gray-800`}>{form === 'cancel' ? 'Cancel the meeting' : 'Close: not converted'}</button>
        </div>
      )}
      {form === 'result' && (
        <div className="rounded-xl border-2 border-purple-100 bg-purple-50/40 p-3 space-y-2">
          <label className={labelCls} htmlFor={`s-${c.id}`}>Score (optional)</label>
          <input id={`s-${c.id}`} value={score} onChange={e => setScore(e.target.value)} className={inputCls} placeholder="32/50" />
          <div className="flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => act('p2pResult', { result: 'Pass', score }, 'Passed: the balance link went to the student')} className={`${btnGo} bg-green-600 hover:bg-green-700`}>✅ Pass</button>
            <button disabled={busy} onClick={() => act('p2pResult', { result: 'Fail', score }, 'Failed: offer the ATS resume, or refund')} className={`${btnGo} bg-rose-600 hover:bg-rose-700`}>❌ Fail</button>
            <button disabled={busy} onClick={() => act('p2pResult', { result: 'No show' }, "Didn't come: set a new test time")} className={btnLine}>🙈 Didn't come</button>
          </div>
        </div>
      )}
      {form === 'restDate' && (
        <div className="rounded-xl border-2 border-purple-100 bg-purple-50/40 p-3 space-y-2">
          <label className={labelCls} htmlFor={`rd-${c.id}`}>The date for the rest of the balance</label>
          <input id={`rd-${c.id}`} type="date" value={restDay} min={istDay(1)} max={istDay(366)} onChange={e => setRestDay(e.target.value)} className={inputCls} />
          <p className="text-[11px] text-gray-500">{c.name} gets an email with the new date.</p>
          <button disabled={busy || !restDay} onClick={() => act('p2pRestDate', { date: restDay }, 'Date saved: the student gets an email')} className={btnGo} style={purpleBg}>Save and email it</button>
        </div>
      )}
      {form === 'ats' && (
        <div className="rounded-xl border-2 border-purple-100 bg-purple-50/40 p-3 space-y-2">
          <label className={labelCls} htmlFor={`a-${c.id}`}>The ATS resume (PDF or Word)</label>
          <input id={`a-${c.id}`} type="file" accept=".pdf,.doc,.docx" onChange={e => setAtsFile(e.target.files?.[0] || null)} className="text-sm" />
          <p className="text-[11px] text-gray-500">Make it in ATS Resume AI, download it, and pick it here. It goes to {c.email || 'the student'} by email.</p>
          <button disabled={busy || !atsFile} onClick={sendAts} className={btnGo} style={purpleBg}>{busy ? 'Sending…' : 'Send it'}</button>
        </div>
      )}
    </article>
  );
};

const P2PPaymentsList: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.p2pPayments>> | null>(null);
  const [getting, setGetting] = useState('');
  useEffect(() => { crm.p2pPayments(auth).then(setData).catch((e: any) => toast.error(e.message)); }, [auth]);
  if (!data) return <Spinner label="Loading payments…" />;
  const t = data.totals;
  // The same receipt the student got by email, to send again (WhatsApp) or keep
  const receipt = async (paymentId: string) => {
    setGetting(paymentId);
    try {
      const r = await crm.p2pReceipt(auth, { paymentId });
      savePdf(r.pdfBase64, r.fileName);
    } catch (e: any) { toast.error(e.message); }
    finally { setGetting(''); }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[['Paid', t.paid], ['Refunded', t.refunded], ['Net', t.net], ['Test fees', t.byKind.test || 0]].map(([k, v]) => (
          <div key={k} className="rounded-2xl bg-white border-2 border-gray-100 p-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">{k}</p>
            <p className="text-lg font-bold text-gray-900">{rupees(Number(v))}</p>
          </div>
        ))}
      </div>
      {!data.list.length ? <Empty icon="💳" title="No P2P payments yet" /> : (
        <div className="overflow-x-auto rounded-2xl border-2 border-gray-100 bg-white">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
              {['Paid on', 'Student', 'For', 'Amount', 'Payment ID', 'Refund', 'Receipt'].map(h => <th key={h} className="px-3 py-2 font-bold">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.list.map(p => (
                <tr key={p.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtWhen(p.paidAt)}</td>
                  <td className="px-3 py-2">{p.name} <span className="text-gray-400">#{p.leadNo}</span></td>
                  <td className="px-3 py-2">{p.label}</td>
                  <td className="px-3 py-2 font-bold">{rupees(p.amount)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{p.paymentId}{p.method ? ` · ${p.method}` : ''}</td>
                  <td className="px-3 py-2 text-xs">{p.refund ? `${rupees(p.refund.amount)} · ${p.refund.status === 'processed' ? 'done' : p.refund.status} · ${p.refund.byName}` : '—'}</td>
                  <td className="px-3 py-2">
                    <button type="button" disabled={!!getting} onClick={() => receipt(p.id)} className="text-xs font-bold text-purple-700 hover:underline disabled:opacity-50 whitespace-nowrap">
                      {getting === p.id ? 'Getting…' : '📄 PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const P2PPricesForm: React.FC<{ auth: CrmAuth; isAdmin: boolean }> = ({ auth, isAdmin }) => {
  const [prices, setPrices] = useState<P2PPrices | null>(null);
  const [ready, setReady] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { crm.p2pSettings(auth).then(r => { setPrices(r.prices); setReady(r.payments); }).catch((e: any) => toast.error(e.message)); }, [auth]);
  if (!prices) return <Spinner />;
  const set = (k: keyof P2PPrices, v: string) => setPrices({ ...prices, [k]: Number(v.replace(/\D/g, '')) || 0 });
  const save = async () => {
    setBusy(true);
    try { setPrices((await crm.p2pSaveSettings(auth, prices)).prices); toast.success('Saved: new links use these prices'); }
    catch (e: any) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const field = (k: keyof P2PPrices, label: string, hint: string) => (
    <div>
      <label className={labelCls} htmlFor={`price-${k}`}>{label}</label>
      <input id={`price-${k}`} inputMode="numeric" value={prices[k]} disabled={!isAdmin} onChange={e => set(k, e.target.value)} className={inputCls} />
      <p className="text-[11px] text-gray-400 mt-1">{hint}</p>
    </div>
  );
  return (
    <div className="rounded-2xl bg-white border-2 border-gray-100 p-4 space-y-4 max-w-xl">
      {!ready && <p className="text-xs font-bold text-red-700 bg-red-50 rounded-xl px-3 py-2">Online payment is not set up yet: add the Razorpay keys (RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET, RAZORPAY_WEBHOOK_SECRET) in Railway.</p>}
      {field('p2pTestFee', 'Test fee (₹)', 'Paid after the meeting; refunded if they fail and don\'t take ATS')}
      {field('p2pBalance', 'Balance after a pass (₹)', `Before placement in all: ${rupees(prices.p2pTestFee + prices.p2pBalance)}`)}
      {field('p2pAfterFee', 'After placement (₹)', `Shown to the student, not collected here. In all: ${rupees(prices.p2pTestFee + prices.p2pBalance + (prices.p2pAfterFee || 0))}`)}
      {field('p2pAtsPrice', 'ATS resume (₹)', `The test fee they paid comes off it: ${rupees(Math.max(0, prices.p2pAtsPrice - prices.p2pTestFee))} after the test fee`)}
      {isAdmin ? <button disabled={busy} onClick={save} className={`${btnGo} px-5`} style={purpleBg}>Save</button>
        : <p className="text-xs text-gray-500">Only the admin changes the prices.</p>}
      <p className="text-[11px] text-gray-400">A link already sent keeps its amount. The landing page shows the test fee, the balance and the after-placement fee.</p>
    </div>
  );
};

// The admin's terms and conditions, one per line (like the CRM's lists on
// Rules): numbered on every P2P receipt (the PDF emailed when a student pays)
// and shown on the payment page before paying. Website only.
const P2PTermsForm: React.FC<{ auth: CrmAuth; isAdmin: boolean }> = ({ auth, isAdmin }) => {
  const [text, setText] = useState<string | null>(null);
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState<'' | 'save' | 'preview'>('');
  useEffect(() => {
    crm.p2pSettings(auth).then(r => { const t = (r.terms || []).join('\n'); setText(t); setSaved(t); }).catch((e: any) => toast.error(e.message));
  }, [auth]);
  if (text === null) return <Spinner />;
  const lines = text.split('\n').map(s => s.trim()).filter(Boolean);
  const changed = lines.join('\n') !== saved;
  const save = async () => {
    setBusy('save');
    try {
      const t = (await crm.p2pSaveTerms(auth, lines)).terms.join('\n');
      setText(t);
      setSaved(t);
      toast.success('Saved: receipts from now on show these terms');
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(''); }
  };
  // A sample test-fee receipt with the box as it is now, saved or not
  const preview = async () => {
    const win = window.open('', '_blank');
    setBusy('preview');
    try { showPdf((await crm.p2pReceipt(auth, { sample: true, terms: lines })).pdfBase64, win); }
    catch (e: any) { win?.close(); toast.error(e.message); }
    finally { setBusy(''); }
  };
  return (
    <div className="rounded-2xl bg-white border-2 border-gray-100 p-4 space-y-3 max-w-3xl">
      <div>
        <label className={labelCls} htmlFor="p2p-terms">Terms & Conditions <span className="normal-case font-normal">(one per line)</span></label>
        <textarea id="p2p-terms" rows={14} value={text} readOnly={!isAdmin} onChange={e => setText(e.target.value)}
          placeholder={isAdmin ? 'Type each term on its own line' : ''} className={`${inputCls} resize-y ${isAdmin ? '' : 'bg-gray-50'}`} />
        <p className="text-[11px] text-gray-400 mt-1">
          {lines.length} {lines.length === 1 ? 'term' : 'terms'}, numbered 1, 2, 3… on the receipt. Every P2P receipt (the PDF emailed when a student pays) lists them, and the payment page shows them before the student pays. A receipt already sent keeps the terms it had. Use English letters (₹ prints as Rs.).
        </p>
      </div>
      {!lines.length && <p className="text-xs font-semibold text-amber-800 bg-amber-50 rounded-xl px-3 py-2">No terms yet: until you add them, receipts point to vistatech.org/Terms-and-Conditions.</p>}
      <div className="flex flex-wrap items-center gap-2">
        {isAdmin && <button type="button" disabled={!!busy || !changed} onClick={save} className={`${btnGo} px-5`} style={purpleBg}>{busy === 'save' ? 'Saving…' : 'Save'}</button>}
        <button type="button" disabled={!!busy} onClick={preview} className={btnLine}>{busy === 'preview' ? 'Making it…' : '📄 Preview a receipt'}</button>
        {isAdmin && changed && <span className="text-xs font-semibold text-amber-700">Not saved yet</span>}
      </div>
      {!isAdmin && <p className="text-xs text-gray-500">Only the admin changes the terms.</p>}
    </div>
  );
};

// The P2P page: managers get the board's tabs; a salesperson, what they booked
const P2PBoard: React.FC<{ auth: CrmAuth; isManager: boolean; isAdmin: boolean; tab?: string; onTab: (t: string) => void }> = ({ auth, isManager, isAdmin, tab, onTab }) => {
  const tabs = isManager ? ['take', 'mine', 'paid', 'open', 'done', 'payments', 'prices', 'terms'] : ['booked'];
  const current = tabs.includes(tab || '') ? tab! : tabs[isManager ? 1 : 0];
  const settingsTab = ['payments', 'prices', 'terms'].includes(current);
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.p2pList>> | null>(null);
  const load = useCallback(async () => {
    if (['payments', 'prices', 'terms'].includes(current)) return;
    try { setData(await crm.p2pList(auth, current as P2PTab)); } catch (e: any) { toast.error(e.message); }
  }, [auth, current]);
  useEffect(() => { setData(null); load(); }, [load]);
  const changed = (c: P2PCase) => setData(d => (d ? { ...d, cases: d.cases.map(x => (x.id === c.id ? c : x)) } : d));
  const counts = data?.counts;
  const label: Record<string, string> = { take: 'To take', mine: 'My students', paid: 'Paid students', open: 'All open', done: 'Done', payments: 'Payments', prices: 'Prices', terms: 'Terms & Conditions', booked: 'My P2P bookings' };
  const empty: Record<string, string> = {
    take: 'No P2P meeting is waiting to be taken', mine: 'No P2P student is yours right now', paid: 'None of your P2P students has paid the test fee yet',
    open: 'No open P2P student', done: 'Nothing finished in the last 4 months', booked: 'Book one from a lead: Log call → P2P Meeting',
  };
  const finished = current === 'paid' && data ? data.cases.findIndex(c => c.step === 'done') : -1;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">🎯 Path to Placement</h1>
          <p className="text-sm text-gray-500">{!isManager ? 'The P2P meetings you booked and where each student is.'
            : current === 'paid' ? 'Your students who paid the test fee. You and the Career Strategist both do the steps: the test, the result, the balance, ATS or a refund.'
            : 'Take a meeting: the link, joined or not, the test fee. Once they pay, they move to Paid students, with a Career Strategist too.'}</p>
        </div>
        {!settingsTab && <button onClick={load} className={btnLine}>↻ Refresh</button>}
      </div>
      {isManager && (
        <Tabs label="P2P" value={current} onChange={onTab} tabs={tabs.map(t => ({
          id: t, label: label[t], badge: t === 'take' ? counts?.take : t === 'mine' ? counts?.mine : t === 'paid' ? counts?.paid : undefined,
        }))} />
      )}
      {data && !data.payments && !settingsTab && (
        <p className="text-xs font-bold text-red-700 bg-red-50 rounded-xl px-3 py-2">Online payment is not set up yet (the Razorpay keys are missing on the server), so students can't pay the links.</p>
      )}
      {current === 'payments' ? <P2PPaymentsList auth={auth} />
        : current === 'prices' ? <P2PPricesForm auth={auth} isAdmin={isAdmin} />
        : current === 'terms' ? <P2PTermsForm auth={auth} isAdmin={isAdmin} />
        : !data ? <Spinner label="Loading…" />
        : !data.cases.length ? <Empty icon="🎯" title={empty[current] || 'Nothing here'} />
        : finished < 0 ? <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{data.cases.map(c => <P2PCard key={c.id} auth={auth} c={c} onChange={changed} />)}</div>
        // Paid students: the ones still going, then the finished ones
        : <>
          {finished > 0 && <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{data.cases.slice(0, finished).map(c => <P2PCard key={c.id} auth={auth} c={c} onChange={changed} />)}</div>}
          <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400 pt-2">Finished (last 4 months)</h2>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">{data.cases.slice(finished).map(c => <P2PCard key={c.id} auth={auth} c={c} onChange={changed} />)}</div>
        </>}
    </div>
  );
};

export default P2PBoard;
